import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { HERO_IMAGES, CATEGORY_ICON } from "@/data/brand";
import ListingCard from "@/components/ListingCard";
import TopClicked from "@/components/TopClicked";

export default function Home() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topClicked, setTopClicked] = useState([]);
  const [total, setTotal] = useState(0);
  const [slotStatus, setSlotStatus] = useState(null);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/listings/featured?limit=8").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/listings/top-clicked?limit=10").then((r) => setTopClicked(r.data)).catch(() => {});
    api.get("/featured/status").then((r) => setSlotStatus(r.data)).catch(() => {});
    api.get("/listings?limit=200").then((r) => setTotal(r.data.length)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (city.trim()) params.set("city", city.trim());
    nav(`/annunci?${params.toString()}#results`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO — centered, modern, mobile-first */}
      <section className="relative overflow-hidden border-b border-zinc-900" data-testid="hero-section">
        {/* Clean layered background: pure black + soft brand glows. No photo, no grain, no lines. */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0" style={{ background: "radial-gradient(1000px 500px at 50% 10%, rgba(255,190,0,0.18), transparent 65%), radial-gradient(800px 400px at 50% 95%, rgba(230,25,168,0.16), transparent 65%), radial-gradient(600px 300px at 15% 60%, rgba(230,25,168,0.10), transparent 70%), radial-gradient(600px 300px at 85% 40%, rgba(255,190,0,0.10), transparent 70%)" }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-12 md:pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FFBE00] mb-5 md:mb-7 hm-reveal" data-testid="hero-eyebrow">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFBE00] animate-pulse"></span>
            <span>La piazza dei professionisti degli affitti brevi</span>
          </div>

          <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] hm-reveal" style={{ animationDelay: '0.1s' }} data-testid="hero-title">
            Trova il tuo <span className="hm-gradient-text">professionista</span>.
            <br />
            Lancia il tuo <span className="text-[#E619A8]">annuncio</span>.
          </h1>

          <p className="mt-5 md:mt-6 mx-auto max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed hm-reveal" style={{ animationDelay: '0.2s' }} data-testid="hero-subtitle">
            Host, property manager, B&B: collegati con fotografi, home stager, revenue manager, avvocati e tutti i professionisti degli affitti brevi in Italia.
          </p>

          <p className="mt-3 mx-auto max-w-xl text-xs md:text-sm text-zinc-500 hm-reveal" style={{ animationDelay: '0.28s' }} data-testid="hero-vdh">
            Un prodotto dell&apos;ecosistema <a href="https://vitadahost.it" target="_blank" rel="noreferrer" className="text-[#FFBE00] hover:text-[#E619A8] font-semibold">vitadahost.it</a>
          </p>

          {/* SEARCH BAR — big, centered, mobile-first */}
          <form
            onSubmit={submitSearch}
            className="mt-7 md:mt-10 relative w-full max-w-2xl mx-auto hm-reveal"
            style={{ animationDelay: '0.36s' }}
            data-testid="home-search-form"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-[#FFBE00] via-[#E619A8] to-[#FFBE00] rounded-2xl opacity-40 blur-lg pointer-events-none"></div>
            <div className="relative bg-black/95 backdrop-blur-md border-2 border-[#FFBE00] rounded-2xl p-2 md:p-2.5 shadow-[0_10px_60px_-10px_rgba(255,190,0,0.35)]">
              <div className="flex flex-col md:flex-row gap-2 items-stretch">
                <div className="flex-1 relative">
                  <i className="fas fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-[#FFBE00] text-lg pointer-events-none"></i>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cosa cerchi?"
                    className="w-full bg-transparent border-0 rounded-xl pl-12 pr-3 py-4 md:py-4 text-base font-semibold text-white placeholder-zinc-500 focus:outline-none focus:ring-0"
                    data-testid="home-search-q"
                  />
                </div>
                <div className="flex-1 md:max-w-[200px] relative md:border-l md:border-zinc-800">
                  <i className="fas fa-location-dot absolute left-4 top-1/2 -translate-y-1/2 text-[#E619A8] text-lg pointer-events-none"></i>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Dove?"
                    className="w-full bg-transparent border-0 rounded-xl pl-12 pr-3 py-4 md:py-4 text-base font-semibold text-white placeholder-zinc-500 focus:outline-none focus:ring-0"
                    data-testid="home-search-city"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    aria-label="Cerca"
                    className="flex-shrink-0 h-14 md:h-auto md:w-14 md:aspect-square rounded-xl bg-[#FFBE00] text-black hover:bg-[#E619A8] hover:text-white transition-all shadow-lg flex items-center justify-center text-xl active:scale-95 flex-1 md:flex-initial"
                    data-testid="home-search-submit"
                  >
                    <i className="fas fa-magnifying-glass"></i>
                  </button>
                  <Link
                    to="/annunci"
                    className="flex-1 md:flex-initial px-5 h-14 md:h-auto rounded-xl bg-[#E619A8] text-yellow-300 border-2 border-[#E619A8] hover:bg-[#FF2BB8] hover:text-yellow-200 transition-all text-xs font-black uppercase tracking-wider flex items-center justify-center whitespace-nowrap shadow-lg"
                    data-testid="hero-cta-browse"
                  >
                    Sfoglia tutto
                  </Link>
                </div>
              </div>
            </div>
          </form>

          <div className="mt-4 md:mt-5 text-xs md:text-sm text-zinc-400 hm-reveal" style={{ animationDelay: '0.44s' }} data-testid="hero-tagline-online">
            <i className="fas fa-bolt text-[#FFBE00] mr-1.5"></i>
            <span>Vai online in <strong className="text-white">2 minuti</strong>.</span>
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-4 gap-2 md:gap-6 max-w-3xl mx-auto">
            {[
              { n: "18", l: "Categorie" },
              { n: total > 0 ? `${total}+` : "100+", l: "Professionisti" },
              { n: "24/7", l: "Supporto" },
              { n: "IT", l: "Made in Italy" },
            ].map((s, i) => (
              <div key={s.l} className="text-center hm-reveal" style={{ animationDelay: `${0.52 + i * 0.06}s` }}>
                <div className="font-heading font-black text-2xl md:text-4xl lg:text-5xl leading-tight hm-gradient-text">{s.n}</div>
                <div className="text-[9px] md:text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* marquee strip */}
        <div className="border-t border-zinc-900 bg-black overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-3 text-xs uppercase tracking-[0.3em] text-zinc-500">
            {Array(2).fill(0).map((_, k) => (
              <div key={k} className="flex items-center gap-10 px-5 shrink-0">
                <span>· Property Manager</span><span className="text-[#FFBE00]">◆</span>
                <span>· Home Stager</span><span className="text-[#E619A8]">◆</span>
                <span>· Fotografi</span><span className="text-[#FFBE00]">◆</span>
                <span>· Architetti</span><span className="text-[#E619A8]">◆</span>
                <span>· Revenue Manager</span><span className="text-[#FFBE00]">◆</span>
                <span>· Avvocati</span><span className="text-[#E619A8]">◆</span>
                <span>· Commercialisti</span><span className="text-[#FFBE00]">◆</span>
                <span>· Social Media</span><span className="text-[#E619A8]">◆</span>
                <span>· Web Designer</span><span className="text-[#FFBE00]">◆</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1️⃣ FEATURED LISTINGS — Annunci più visibili (8 slot fissi, placeholder tratteggiati se vuoti) */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="featured-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#FFBE00] mb-2">· Home Spotlight</div>
            <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">Annunci più <span className="text-[#FFBE00]">visibili</span>.</h2>
            <p className="text-zinc-400 text-sm mt-2">Solo <strong className="text-white">8 posti</strong> in home page. Massima visibilità, massima resa.</p>
          </div>
          <Link to="/prezzi" className="text-sm text-zinc-400 hover:text-[#FFBE00] uppercase tracking-wider">Scopri i piani →</Link>
        </div>

        {/* LIVE COUNTER — FOMO band */}
        {slotStatus && <LiveSlotCounter status={slotStatus} />}

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {Array.from({ length: 8 }).map((_, i) => {
            const listing = featured[i];
            if (listing) {
              return (
                <div
                  key={listing.id}
                  className="animate-fade-up"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  <ListingCard listing={listing} />
                </div>
              );
            }
            return <FeaturedSlotPlaceholder key={`slot-${i}`} index={i} />;
          })}
        </div>
      </section>

      {/* 2️⃣ TOP 10 CLICKED — Annunci più cliccati */}
      <TopClicked items={topClicked} />

      {/* 3️⃣ CATEGORIES BENTO — Il tuo settore */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="categories-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#E619A8] mb-2">· Categorie</div>
            <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">Il tuo <span className="text-[#FFBE00]">settore</span>.</h2>
          </div>
          <Link to="/categorie" className="text-sm text-zinc-400 hover:text-[#FFBE00] uppercase tracking-wider">Tutte →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.slice(0, 12).map((c, i) => (
            <Link
              key={c.slug}
              to={`/categoria/${c.slug}`}
              className={`hm-card p-5 flex flex-col gap-2 animate-fade-up ${i % 5 === 0 ? "hm-card-magenta" : ""}`}
              style={{ animationDelay: `${i * 0.04}s` }}
              data-testid={`category-card-${c.slug}`}
            >
              <i className={`fas ${CATEGORY_ICON[c.slug] || "fa-briefcase"} text-2xl ${i % 5 === 0 ? "text-[#E619A8]" : "text-[#FFBE00]"} transition-transform group-hover:scale-110`}></i>
              <div className="font-heading font-bold text-base leading-tight">{c.name}</div>
              <div className="text-xs text-zinc-500 mt-auto">da €{c.pricing.monthly}/mese</div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative border-y border-zinc-900 mt-8" data-testid="home-cta-band">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E619A8] mb-3">· Per i professionisti</div>
            <h3 className="font-heading font-black text-3xl md:text-5xl leading-tight mb-5">
              Entra nella piattaforma <br /> più <span className="text-[#FFBE00]">tech</span> d&apos;Italia.
            </h3>
            <p className="text-zinc-400 leading-relaxed mb-6 max-w-lg">Pubblica il tuo annuncio con foto, video, link, mappa e contatti diretti. Abbonamenti mensili o annuali, scegli tu.</p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/prezzi" className="px-6 py-3 rounded-md bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#FFBE00] hover:text-black transition-all" data-testid="cta-pricing">
                Vedi prezzi
              </Link>
              <Link to="/registrati" className="px-6 py-3 rounded-md border border-zinc-700 text-white font-bold uppercase tracking-wider text-sm hover:border-[#E619A8] hover:text-[#E619A8]" data-testid="cta-register">
                Iscriviti gratis
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["fa-bolt", "Online in 2 min"],
              ["fa-shield-halved", "Pagamenti sicuri"],
              ["fa-chart-line", "Più visibilità"],
              ["fa-message", "Contatto diretto"],
            ].map(([icon, label], i) => (
              <div key={label} className="hm-glass p-5 hm-reveal hover:-translate-y-1 hover:border-[#FFBE00]/60" style={{ animationDelay: `${i * 0.08}s` }}>
                <i className={`fas ${icon} text-[#FFBE00] text-xl mb-2`}></i>
                <div className="font-heading font-bold text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 step modern strip */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="how-it-works">
        <div className="text-xs uppercase tracking-widest text-[#E619A8] mb-3">· Come funziona</div>
        <h2 className="font-heading font-black text-3xl md:text-4xl leading-tight mb-8">
          Da zero a <span className="hm-gradient-text">visibile</span> in 3 passi.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { n: "01", icon: "fa-user-plus", title: "Registrati gratis", body: "Crea il tuo account in 30 secondi. Nessuna carta richiesta." },
            { n: "02", icon: "fa-file-lines", title: "Pubblica l'annuncio", body: "Aggiungi foto, video, link, mappa e contatti diretti. Scegli il piano a partire da 6,99€/mese." },
            { n: "03", icon: "fa-arrow-trend-up", title: "Ricevi contatti", body: "Host e property manager ti contattano via email, WhatsApp o telefono. Zero intermediari." },
          ].map((s, i) => (
            <div key={s.n} className="hm-glass p-6 hm-reveal hover:-translate-y-1" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl font-heading font-black hm-gradient-text leading-none">{s.n}</span>
                <i className={`fas ${s.icon} text-[#FFBE00] text-2xl`}></i>
              </div>
              <div className="font-heading font-bold text-lg mb-2">{s.title}</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Live FOMO counter rendered above the 8 featured slots.
// Shows occupied count, waitlist size, and a progress bar with intensity by fill level.
function LiveSlotCounter({ status }) {
  const { total_slots: total, active, available, waitlist_count: waitlist } = status;
  const pct = Math.round((active / total) * 100);
  const urgency = available <= 2 ? "critical" : available <= 4 ? "warm" : "cool";
  const urgencyColor = urgency === "critical" ? "#EF4444" : urgency === "warm" ? "#E619A8" : "#FFBE00";
  const urgencyLabel = urgency === "critical" ? "🔥 Quasi esaurito" : urgency === "warm" ? "⚡ In esaurimento" : "✨ Tutti i posti liberi";
  const availableLabel = available === 1 ? "1 slot disponibile" : `${available} slot disponibili`;

  return (
    <div
      className="mb-6 rounded-xl border p-4 md:p-5 flex flex-wrap items-center gap-4"
      style={{
        borderColor: `${urgencyColor}40`,
        background: `linear-gradient(90deg, ${urgencyColor}0F 0%, transparent 60%), #0A0A0A`,
      }}
      data-testid="featured-live-counter"
    >
      <span
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: `${urgencyColor}20`, color: urgencyColor }}
      >
        <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: urgencyColor, boxShadow: `0 0 10px ${urgencyColor}` }}></span>
        Live · {urgencyLabel}
      </span>

      <div className="flex-1 min-w-[200px]">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-heading font-black text-2xl md:text-3xl">{active}/{total}</span>
          <span className="text-xs uppercase tracking-wider text-zinc-500">occupati</span>
          <span className="text-zinc-700">·</span>
          <span className="text-sm" style={{ color: urgencyColor }}><strong>{availableLabel}</strong></span>
          {waitlist > 0 && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="text-sm text-zinc-300">
                <i className="fas fa-user-clock mr-1 text-[#FFBE00]"></i>
                <strong className="text-white">{waitlist}</strong> in waitlist
              </span>
            </>
          )}
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-zinc-900 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${urgencyColor} 0%, #FFBE00 100%)`, boxShadow: `0 0 12px ${urgencyColor}` }}
          ></div>
        </div>
      </div>

      <Link
        to="/dashboard/nuovo"
        className="text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-md transition-all whitespace-normal text-center leading-tight max-w-[240px]"
        style={{ backgroundColor: urgencyColor, color: urgency === "cool" ? "black" : "white" }}
        data-testid="featured-live-counter-cta"
      >
        <i className="fas fa-bolt mr-1"></i> Pubblica il tuo annuncio<br />e aggiungi il boost
      </Link>
    </div>
  );
}

// Dashed marketing placeholder rendered in empty home slots.
// Invites pros to buy the €9,90 · 30gg Home Spotlight.
// Intentionally compact on mobile (2-col grid) to save vertical space.
function FeaturedSlotPlaceholder({ index }) {
  return (
    <Link
      to="/dashboard/nuovo"
      className="group relative block h-full min-h-[180px] md:min-h-[340px] rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#FFBE00] bg-[#0A0A0A] p-3 md:p-6 flex flex-col transition-all hover:bg-gradient-to-br hover:from-[#FFBE00]/5 hover:to-[#E619A8]/5 animate-fade-up"
      style={{ animationDelay: `${index * 0.06}s` }}
      data-testid={`featured-placeholder-${index}`}
    >
      <div className="flex items-start justify-between mb-1 md:mb-4">
        <span className="inline-block w-6 h-6 md:w-8 md:h-8 rounded-full border border-zinc-700 text-zinc-500 text-[10px] md:text-xs font-bold uppercase flex items-center justify-center group-hover:border-[#FFBE00] group-hover:text-[#FFBE00]">
          {index + 1}
        </span>
        <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#FFBE00] font-bold">Libero</span>
      </div>

      <i className="fas fa-bolt text-xl md:text-4xl text-zinc-700 group-hover:text-[#FFBE00] group-hover:scale-110 transition-transform mb-2 md:mb-4 self-center mt-2 md:mt-6"></i>

      <div className="font-heading font-black text-sm md:text-xl leading-tight text-center text-zinc-300 group-hover:text-white">
        <span className="md:hidden">Slot libero</span>
        <span className="hidden md:inline">Prenota il <span className="text-[#FFBE00]">tuo posto</span>.</span>
      </div>
      <p className="hidden md:block text-xs text-zinc-500 text-center mt-2 group-hover:text-zinc-400">
        Solo <strong className="text-white">8 posti</strong> in home page.<br />
        Massima visibilità, massima resa.
      </p>

      <div className="mt-auto pt-2 md:pt-5 text-center">
        <div className="font-heading font-black text-base md:text-2xl text-[#FFBE00]">€9,90</div>
        <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 md:mt-1">30 giorni</div>
        <div className="mt-2 md:mt-4 inline-block px-2.5 md:px-4 py-1 md:py-2 rounded-md bg-[#FFBE00] text-black text-[10px] md:text-xs font-bold uppercase tracking-wider group-hover:bg-[#E619A8] group-hover:text-white transition-colors">
          Attiva →
        </div>
        <p className="hidden md:block text-[10px] text-zinc-500 mt-3 leading-snug">
          Attivalo durante la <strong className="text-zinc-300">creazione del tuo annuncio</strong> o anche dopo dalla dashboard.
        </p>
      </div>
    </Link>
  );
}
